package scenes

import androidx.compose.foundation.background
import androidx.compose.foundation.focusable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.dp
import audio.LocalSoundEngine
import domain.MODE_CONFIGS
import domain.PlayGameMode
import domain.cards.CardDefinition
import domain.cards.CardEffect
import domain.cards.CardRepository
import domain.cards.RunState
import domain.garbage.StoryGarbageManager
import domain.levels.RoomObjective
import domain.score.StoryCurrencyCalculator
import domain.player.GameAction
import domain.player.GraphicsConfigStore
import domain.player.KeyBindingStore
import engine.model.GameSnapshot
import engine.model.MoveSource
import engine.model.SpinType
import engine.model.events.DefaultGameEvents
import engine.model.events.EventOrchestrator
import engine.model.events.GameEvent
import game.GameMode
import game.GravitarGame
import game.events.GravitarEvents
import rendering.ComboDisplay
import rendering.AnimatedBackground
import rendering.BLOCK_SIZE
import rendering.BOARD_COLS
import rendering.BOARD_ROWS
import rendering.BoardCanvas
import rendering.DebrisState
import rendering.GameLoop
import rendering.GameOverlay
import rendering.GarbagePendingBar
import rendering.HoldBox
import rendering.LockFlashState
import rendering.NextBox
import rendering.ScoreFeedDisplay
import rendering.ScoreFeedEntry
import rendering.ShakeState
import rendering.StatLabel
import rendering.StatValue
import rendering.TiltState
import rendering.TopBar
import rendering.WiggleState
import rendering.touchGameInput

/**
 * [StoryScene] — full story-mode game layout.
 *
 * Extends the standard game layout with:
 * - A [GarbagePendingBar] to the right of the board
 * - A [CardPickScene] overlay triggered by [GravitarEvents.RoomObjectiveMet]
 * - Story-specific coin tracking via [GravitarEvents.CoinsAwarded]
 * - Pending garbage tracking via [GravitarEvents.BlockedGarbageSpawned]
 * - Restart-run logic that creates a fresh [GravitarGame] instance
 *
 * @param navigate Callback to navigate to another scene.
 */
@Composable
fun StoryScene(navigate: Navigate) {
    val focusRequester = remember { FocusRequester() }

    // Create the story game — replaced on retry to start a fresh run
    var game by remember {
        mutableStateOf(
            GravitarGame(gameId = "story-run") {
                mode = GameMode.CUSTOM
            }
        )
    }

    var snapshot by remember { mutableStateOf<GameSnapshot?>(null) }
    val debrisState = remember { DebrisState() }
    val shakeState = remember { ShakeState() }
    val tiltState = remember { TiltState() }
    val wiggleState = remember { WiggleState() }
    val lockFlashState = remember { LockFlashState() }
    val coroutineScope = rememberCoroutineScope()
    val graphicsConfig = remember { GraphicsConfigStore.load() }

    // HUD state
    var b2bCount by remember { mutableStateOf(0) }
    var comboCount by remember { mutableStateOf(0) }
    val scoreFeedEntries = remember { mutableStateListOf<ScoreFeedEntry>() }
    var nextFeedId by remember { mutableStateOf(0L) }

    // Story-specific state
    var pendingGarbageLines by remember { mutableStateOf(0) }
    var currentCoins by remember { mutableStateOf(0) }
    val garbageManager = remember { StoryGarbageManager(cols = BOARD_COLS) }

    // Room progression state
    val runState = remember { RunState() }
    var currentRoom by remember { mutableStateOf(1) }
    var roomLinesCleared by remember { mutableStateOf(0) }
    var roomScoreAccumulated by remember { mutableStateOf(0.0) }
    var roomSpinsLanded by remember { mutableStateOf(0) }
    var roomDifficultClears by remember { mutableStateOf(0) }
    var roomPiecesPlaced by remember { mutableStateOf(0) }
    var objectiveMet by remember { mutableStateOf(false) }

    // Card pick overlay state
    var showCardPick by remember { mutableStateOf(false) }
    var cardOptions by remember { mutableStateOf<List<CardDefinition>>(emptyList()) }

    // Overlay state — show when game is over or goal is met
    val showOverlay = game.isGameOver || game.isGoalMet

    // Key tracking
    val heldKeys = remember { mutableSetOf<Long>() }
    var leftHeld by remember { mutableStateOf(false) }
    var rightHeld by remember { mutableStateOf(false) }

    val activeBindings = remember { KeyBindingStore.loadActive() }
    val moveLeftKeys = remember { activeBindings[GameAction.MOVE_LEFT] ?: emptyList() }
    val moveRightKeys = remember { activeBindings[GameAction.MOVE_RIGHT] ?: emptyList() }

    val modeConfig = MODE_CONFIGS[PlayGameMode.STORY]

    // Game loop — drives game updates and snapshot capture each frame
    GameLoop(game = game) { deltaMs ->
        game.update(deltaMs)
        snapshot = game.getSnapshot()
        debrisState.update(deltaMs.toFloat())
    }

    // Request keyboard focus once on composition
    LaunchedEffect(Unit) { focusRequester.requestFocus() }

    // Re-request focus whenever the card pick overlay closes
    LaunchedEffect(showCardPick) {
        if (!showCardPick) {
            kotlinx.coroutines.delay(50)
            focusRequester.requestFocus()
        }
    }

    val soundEngine = LocalSoundEngine.current

    // Subscribe to game events for HUD updates, sound, and story-specific logic
    DisposableEffect(game) {
        val gameId = game.gameId
        val listeners = mutableListOf<(GameEvent) -> Unit>()

        // Attach the coin calculator — subscribes to ScoreUpdated and awards coins
        val coinCalculator = StoryCurrencyCalculator(gameId, runState)
        coinCalculator.attach()

        fun addListener(listener: (GameEvent) -> Unit) {
            EventOrchestrator.listeners.add(listener)
            listeners.add(listener)
        }

        // Sound: piece moved
        addListener { ev ->
            if (ev is DefaultGameEvents.PieceMoved && ev.gameId == gameId && ev.moveSource == MoveSource.PLAYER)
                soundEngine.playMove()
        }
        // Wall wiggle: piece didn't move horizontally despite player input
        addListener { ev ->
            if (ev is DefaultGameEvents.PieceMoved && ev.gameId == gameId
                && ev.moveSource == MoveSource.PLAYER && ev.deltaCol == 0
            ) {
                val dir = when {
                    leftHeld -> -1
                    rightHeld -> 1
                    else -> 0
                }
                if (dir != 0) wiggleState.triggerWiggle(coroutineScope, dir)
            }
        }
        // Sound: piece rotated
        addListener { ev ->
            if (ev is DefaultGameEvents.PieceRotated && ev.gameId == gameId)
                soundEngine.playRotate()
        }
        // Tilt: apply spin tilt when a spin is detected during rotation
        addListener { ev ->
            if (ev is DefaultGameEvents.SpinDetected && ev.gameId == gameId && ev.spinType != SpinType.NONE) {
                tiltState.applySpinTilt(coroutineScope, direction = 1, maxDeg = 3.5f)
            }
        }
        // Sound: soft drop
        addListener { ev ->
            if (ev is DefaultGameEvents.SoftDrop && ev.gameId == gameId)
                soundEngine.playSoftDrop()
        }
        // Sound + shake + score feed: hard drop (no feed entry)
        addListener { ev ->
            if (ev is DefaultGameEvents.HardDrop && ev.gameId == gameId) {
                soundEngine.playHardDrop()
                shakeState.triggerShake(coroutineScope, intensity = graphicsConfig.boardShake.toFloat())
            }
        }
        // Sound: piece held
        addListener { ev ->
            if (ev is DefaultGameEvents.PieceHeld && ev.gameId == gameId)
                soundEngine.playHold()
        }
        // Sound: piece locked (no lines cleared)
        addListener { ev ->
            if (ev is DefaultGameEvents.PieceLocked && ev.gameId == gameId && !ev.linesCleared)
                soundEngine.playLock()
        }
        // Lock flash: trigger on every piece lock
        addListener { ev ->
            if (ev is DefaultGameEvents.PieceLocked && ev.gameId == gameId)
                lockFlashState.trigger(coroutineScope)
        }
        // Sound + debris + score feed + pending garbage: line cleared
        addListener { ev ->
            if (ev is DefaultGameEvents.LineCleared && ev.gameId == gameId && ev.linesCleared.isNotEmpty()) {
                val count = ev.linesCleared.size
                soundEngine.playLineClear(count)
                shakeState.triggerShake(coroutineScope, intensity = (graphicsConfig.boardShake * 0.4f).toFloat())
                debrisState.triggerLineClearDebris(
                    clearedRows = ev.linesCleared,
                    getCell = { r, c -> game.board[r, c] },
                )
                // Decrement pending garbage lines when lines are cleared
                pendingGarbageLines = (pendingGarbageLines - count).coerceAtLeast(0)
                // Story-specific clear labels — use StoryRulebook names
                val label = when (count) {
                    1 -> "BLIP"
                    2 -> "BURST"
                    3 -> "FLARE"
                    4 -> "GRAVITAR"
                    in 5..7 -> "GRAVITAR"
                    in 8..11 -> "VOID"
                    in 12..15 -> "COLLAPSE"
                    else -> "EVENT HORIZON"
                }
                val entry = ScoreFeedEntry(id = nextFeedId++, label = label, value = "+$count")
                scoreFeedEntries.add(0, entry)
            }
        }
        // Sound: level up
        addListener { ev ->
            if (ev is DefaultGameEvents.LevelUp && ev.gameId == gameId)
                soundEngine.playLevelUp()
        }
        // Sound: game over
        addListener { ev ->
            if (ev is DefaultGameEvents.GameOver && ev.gameId == gameId)
                soundEngine.playGameOver()
        }
        // HUD: back-to-back counter + B2B badge in feed
        addListener { ev ->
            if (ev is DefaultGameEvents.BackToBackTrigger && ev.gameId == gameId) {
                b2bCount = ev.backToBackCount
                if (ev.backToBackCount == 0) {
                    // Chain just started — add a single badge
                    val entry = ScoreFeedEntry(
                        id = nextFeedId++,
                        label = "B2B",
                        value = ev.backToBackCount.toString(),
                        isB2B = true,
                    )
                    scoreFeedEntries.add(0, entry)
                } else if (ev.backToBackCount < 0) {
                    // Chain broken — clear all stale B2B badges
                    scoreFeedEntries.removeAll { it.isB2B }
                }
            }
        }
        // HUD: combo counter
        addListener { ev ->
            if (ev is DefaultGameEvents.ComboTriggered && ev.gameId == gameId)
                comboCount = ev.comboCount
        }
        // Story: track pending garbage lines spawned by card effects
        addListener { ev ->
            if (ev is GravitarEvents.BlockedGarbageSpawned && ev.gameId == gameId)
                pendingGarbageLines += ev.lines
        }
        // Story: room objective met — draw card options and show card pick overlay
        addListener { ev ->
            if (ev is GravitarEvents.RoomObjectiveMet && ev.gameId == gameId) {
                cardOptions = CardRepository.drawOptions(count = 3, level = ev.room)
                showCardPick = true
            }
        }
        // Story: coins awarded — accumulate coin balance
        addListener { ev ->
            if (ev is GravitarEvents.CoinsAwarded && ev.gameId == gameId)
                currentCoins += ev.amount
        }

        // ── Room objective tracker ─────────────────────────────────────────────
        // Monitors game events and publishes RoomObjectiveMet when the active
        // room objective is satisfied. The default objective is Lines(10).

        // Helper: check and fire objective met (called after each relevant event)
        fun checkObjective() {
            if (objectiveMet) return
            val objective = runState.activeObjective
            val current = when (objective) {
                is RoomObjective.Lines -> roomLinesCleared.toDouble()
                is RoomObjective.Score -> roomScoreAccumulated
                is RoomObjective.DifficultClears -> roomDifficultClears.toDouble()
                is RoomObjective.Spins -> roomSpinsLanded.toDouble()
                is RoomObjective.MaxCombo -> 0.0  // tracked via combo events
                is RoomObjective.PieceSurvival -> roomPiecesPlaced.toDouble()
            }
            if (objective.isMet(current)) {
                objectiveMet = true
                EventOrchestrator.publish(
                    GravitarEvents.RoomObjectiveMet(
                        objective = objective,
                        room = currentRoom,
                        gameId = gameId,
                    )
                )
            }
        }

        // Track lines cleared — LineCleared already carries spinType so we can
        // derive spin clears and difficult clears from a single listener.
        addListener { ev ->
            if (ev is DefaultGameEvents.LineCleared && ev.gameId == gameId && ev.linesCleared.isNotEmpty()) {
                val count = ev.linesCleared.size
                roomLinesCleared += count

                val isSpin = ev.spinType != SpinType.NONE
                if (isSpin) {
                    roomSpinsLanded += 1
                    roomDifficultClears += 1  // spin clears are always difficult
                } else if (count >= 4) {
                    roomDifficultClears += 1  // 4+ line clear without spin
                }

                checkObjective()
            }
        }

        // Track score for Score objective
        addListener { ev ->
            if (ev is DefaultGameEvents.ScoreUpdated && ev.gameId == gameId) {
                roomScoreAccumulated += ev.pointsEarned
                checkObjective()
            }
        }

        // Track pieces placed for PieceSurvival objective
        addListener { ev ->
            if (ev is DefaultGameEvents.PieceLocked && ev.gameId == gameId) {
                roomPiecesPlaced += 1
                checkObjective()
            }
        }

        onDispose {
            coinCalculator.detach()
            EventOrchestrator.listeners.removeAll(listeners)
        }
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black)
            .focusRequester(focusRequester)
            .focusable()
            .onKeyEvent { event ->
                val keyCode = event.key.keyCode
                val keyId = keyCode.toString()
                when (event.type) {
                    KeyEventType.KeyDown -> {
                        if (event.key == androidx.compose.ui.input.key.Key.Escape) {
                            navigate(SceneName.MENU, null)
                            return@onKeyEvent true
                        }
                        val isFresh = heldKeys.add(keyCode)
                        if (keyId in moveLeftKeys) leftHeld = true
                        if (keyId in moveRightKeys) rightHeld = true
                        game.onKeyDown(keyId, isFresh)
                    }

                    KeyEventType.KeyUp -> {
                        heldKeys.remove(keyCode)
                        if (keyId in moveLeftKeys) leftHeld = false
                        if (keyId in moveRightKeys) rightHeld = false
                        game.onKeyUp(keyId)
                    }
                }
                true
            },
    ) {
        // Animated background — behind everything
        AnimatedBackground(modifier = Modifier.fillMaxSize())

        Column(modifier = Modifier.fillMaxSize()) {
            val snap = snapshot
            val elapsedMs = ((snap?.hudInfo?.sessionTimeSeconds ?: 0.0) * 1000).toLong()

            // Top bar: mode name, B2B counter, elapsed time
            TopBar(
                modeName = modeConfig?.label ?: "STORY",
                b2bCount = b2bCount,
                elapsedMs = elapsedMs,
            )

            // Main game row: left panel | board + garbage bar | right panel
            BoxWithConstraints(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f),
            ) {
                val density = LocalDensity.current

                // Fixed panel widths — shrink on narrow screens
                val leftPanelDp = minOf(100.dp, maxWidth * 0.22f)
                val rightPanelDp = minOf(80.dp, maxWidth * 0.18f)
                val garbageBarDp = 16.dp
                val spacingDp = 8.dp  // spacedBy value

                // Available width for the board after panels and spacing
                val boardAvailWidthDp = maxWidth - leftPanelDp - rightPanelDp - garbageBarDp - spacingDp * 4

                // Convert both constraints to px and pick the tighter one
                val availWidthPx = with(density) { boardAvailWidthDp.toPx() }
                val availHeightPx = with(density) { maxHeight.toPx() }
                val bsByWidth = availWidthPx / BOARD_COLS
                val bsByHeight = availHeightPx / BOARD_ROWS
                val bs = minOf(bsByWidth, bsByHeight, BLOCK_SIZE)

                val boardWidthDp = with(density) { (BOARD_COLS * bs).toDp() }
                val boardHeightDp = with(density) { (BOARD_ROWS * bs).toDp() }

                Row(
                    modifier = Modifier.fillMaxSize(),
                    horizontalArrangement = Arrangement.spacedBy(spacingDp, Alignment.CenterHorizontally),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    // ── Left panel ────────────────────────────────────────────────
                    Column(
                        modifier = Modifier
                            .width(leftPanelDp)
                            .wrapContentHeight(),
                        verticalArrangement = Arrangement.spacedBy(8.dp),
                        horizontalAlignment = Alignment.CenterHorizontally,
                    ) {
                        HoldBox(piece = snap?.holdPiece)
                        ComboDisplay(comboCount = comboCount)
                        StatLabel(text = "LEVEL")
                        StatValue(text = snap?.hudInfo?.currentLevel?.toString() ?: "-")
                        StatLabel(text = "COINS")
                        StatValue(text = "$currentCoins")
                        ScoreFeedDisplay(
                            entries = scoreFeedEntries,
                            onRemove = { id -> scoreFeedEntries.removeAll { it.id == id } },
                        )
                    }

                    // ── Board + garbage pending bar ───────────────────────────────
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        if (snap != null) {
                            BoardCanvas(
                                snapshot = snap,
                                blockSize = bs,
                                modifier = Modifier
                                    .size(width = boardWidthDp, height = boardHeightDp)
                                    .touchGameInput(game = game, enabled = !showOverlay && !showCardPick)
                                    .graphicsLayer {
                                        translationX = shakeState.offsetX.value + wiggleState.offsetX.value
                                        translationY = shakeState.offsetY.value
                                        rotationZ = tiltState.rotationZ.value
                                    },
                                debrisState = debrisState,
                                lockFlashColor = lockFlashState.color,
                            )
                        }
                        Spacer(modifier = Modifier.width(4.dp))
                        GarbagePendingBar(
                            pendingLines = pendingGarbageLines,
                            modifier = Modifier.height(boardHeightDp),
                        )
                    }

                    // ── Right panel ───────────────────────────────────────────────
                    Column(
                        modifier = Modifier
                            .width(rightPanelDp)
                            .wrapContentHeight(),
                        verticalArrangement = Arrangement.spacedBy(4.dp),
                        horizontalAlignment = Alignment.CenterHorizontally,
                    ) {
                        for (i in 0 until 5) {
                            NextBox(piece = snap?.nextPieces?.getOrNull(i), index = i)
                        }
                        Spacer(modifier = Modifier.height(8.dp))
                        if (snap != null && modeConfig != null) {
                            StatLabel(text = modeConfig.goalLabel)
                            StatValue(text = modeConfig.getGoalValue(snap))
                        }
                    }
                }
            }
        }

        // Game over / goal met overlay
        GameOverlay(
            visible = showOverlay,
            isGoalMet = game.isGoalMet,
            snap = snapshot,
            modeConfig = game.matchConfig,
            onRetry = {
                // Create a fresh game instance for a new run
                game = GravitarGame(gameId = "story-run") {
                    mode = GameMode.CUSTOM
                }
                debrisState.clear()
                b2bCount = 0
                comboCount = 0
                scoreFeedEntries.clear()
                pendingGarbageLines = 0
                currentCoins = 0
                garbageManager.reset()
                showCardPick = false
                cardOptions = emptyList()
                // Reset room progression
                runState.reset()
                currentRoom = 1
                roomLinesCleared = 0
                roomScoreAccumulated = 0.0
                roomSpinsLanded = 0
                roomDifficultClears = 0
                roomPiecesPlaced = 0
                objectiveMet = false
            },
            onMenu = { navigate(SceneName.MENU, null) },
        )

        // Card pick overlay — shown when a room objective is met
        if (showCardPick && cardOptions.isNotEmpty()) {
            CardPickScene(
                cards = cardOptions,
                coins = currentCoins,
                onCardPicked = { card ->
                    runState.activateCard(card)
                    // Spawn garbage immediately for SpawnBlockedGarbage effects
                    card.effects.filterIsInstance<CardEffect.SpawnBlockedGarbage>().forEach { effect ->
                        garbageManager.queueGarbage(effect.initialLines)
                        garbageManager.flushPendingToBoard(game.board)
                    }
                    // Advance to next room and reset room progress counters
                    currentRoom += 1
                    roomLinesCleared = 0
                    roomScoreAccumulated = 0.0
                    roomSpinsLanded = 0
                    roomDifficultClears = 0
                    roomPiecesPlaced = 0
                    objectiveMet = false
                    showCardPick = false
                },
                onBankruptDismissed = {
                    // Bankrupt: advance room anyway so the run can continue
                    currentRoom += 1
                    roomLinesCleared = 0
                    roomScoreAccumulated = 0.0
                    roomSpinsLanded = 0
                    roomDifficultClears = 0
                    roomPiecesPlaced = 0
                    objectiveMet = false
                    showCardPick = false
                },
            )
        }
    }
}
